/**
 * <Money/> — single source of truth for money rendering across the tenant UI.
 *
 * Usage:
 *
 *   <Money amount={49900} />          → "AED 49,900" (or whatever tenant.currency is)
 *   <Money amount={x} currency="USD" />  → force a specific currency (e.g. per-record
 *                                          override for a target/invoice stored in
 *                                          its own currency, not the tenant default)
 *
 * Internally calls utils/money.formatMoney with the tenant's chosen currency
 * (via useTenantCurrency / BrandingContext).  Reformats automatically whenever
 * admin changes the tenant's currency — no per-component code change needed.
 *
 * Why a component rather than just inline useTenantCurrency() + formatMoney():
 *   - One grep target: "Money amount=" finds every money display in the codebase
 *   - Single place to change formatting policy (decimal precision, locale, etc.)
 *   - Components that just display money don't need to call the hook themselves
 *
 * For non-JSX contexts (e.g. building a label string in a data array), use
 * the useFormatMoney() hook instead:
 *
 *   const fmt = useFormatMoney();
 *   const value = fmt(amount);           // "AED 49,900"
 */
import React from 'react';
import {
  Banknote, DollarSign, Euro, IndianRupee, JapaneseYen, PoundSterling,
} from 'lucide-react';
import { formatMoney, formatMoneyWithWords } from '../utils/money';
import { useTenantCurrency } from '../context/BrandingContext';

// ── The money glyph ──────────────────────────────────────────────────────
//
// Every "this card is about money" icon in the app was a hardcoded
// lucide DollarSign, so an Indian tenant's Pipeline card carried a $ next to
// a figure reading INR 15,80,000 (user, on Praxia staging, 2026-08-06 — and
// it was 18 places, not one).
//
// The currency already drives the number's formatting; it should drive the
// glyph too. Anything we have no symbol for falls back to a generic banknote,
// which is right for AED / NZD / CAD and safe for whatever onboards next —
// never a dollar by default.
const GLYPH_BY_CURRENCY = {
  INR: IndianRupee,
  USD: DollarSign,
  CAD: DollarSign,
  AUD: DollarSign,
  NZD: DollarSign,
  SGD: DollarSign,
  EUR: Euro,
  GBP: PoundSterling,
  JPY: JapaneseYen,
};

/**
 * The tenant's currency glyph. Drop-in replacement for `DollarSign` anywhere
 * an icon marks a money figure — including as a component reference
 * (`icon={MoneyIcon}`), since it forwards size/className like a lucide icon.
 */
export function MoneyIcon(props) {
  const tenantCurrency = useTenantCurrency();
  const Glyph = GLYPH_BY_CURRENCY[tenantCurrency] || Banknote;
  return <Glyph {...props} />;
}

export function Money({ amount, currency }) {
  const tenantCurrency = useTenantCurrency();
  return <>{formatMoney(amount, currency || tenantCurrency)}</>;
}

/**
 * Hook variant for places where JSX isn't usable (e.g. value of a config
 * object passed as a prop, or a chart axis label callback).
 */
export function useFormatMoney() {
  const tenantCurrency = useTenantCurrency();
  return (amount, currency) => formatMoney(amount, currency || tenantCurrency);
}

/**
 * "INR 1,50,00,000 (1.5 crore)" — for the few figures large enough that a run
 * of zeros is genuinely easy to misread: a sanction, a disbursement, a fee.
 * Below a lakh (or a million outside India) the words are omitted, so this is
 * safe to use anywhere a big number MIGHT appear without adding noise when it
 * doesn't.
 */
export function useFormatMoneyWithWords() {
  const tenantCurrency = useTenantCurrency();
  return (amount, currency) => formatMoneyWithWords(amount, currency || tenantCurrency);
}

export default Money;
